import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";

const sectionConfig = {
  workTypes: {
    title: "Work Type Master",
    endpoint: "/api/master/work-types",
    codeLabel: "Code",
    nameLabel: "Work Type Name",
  },
  works: {
    title: "Work Master",
    endpoint: "/api/master/works",
    codeLabel: "Code",
    nameLabel: "Work Name",
  },
  statuses: {
    title: "Enquiry Status Master",
    endpoint: "/api/master/enquiry-statuses",
    codeLabel: "Code",
    nameLabel: "Status Name",
  },
  users: {
    title: "Users Master",
    endpoint: "/api/admin/users",
  },
};

export default function MasterPage({ section }) {
  const { http } = useContext(AuthContext);
  const config = sectionConfig[section] || sectionConfig.workTypes;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workTypes, setWorkTypes] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", workTypeId: "", fee: "" });

  const canSave = useMemo(() => {
    if (section === "works") {
      return form.code.trim() && form.name.trim() && form.workTypeId;
    }
    return form.code.trim() && form.name.trim();
  }, [form, section]);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const requests = [http.get(config.endpoint)];
        if (section === "works") {
          requests.push(http.get("/api/master/work-types"));
        }
        const [res, workTypesRes] = await Promise.all(requests);
        if (alive) {
          setItems(res.data.items);
          if (workTypesRes) {
            setWorkTypes(workTypesRes.data.items);
          }
        }
      } catch (err) {
        if (alive) {
          setError(err?.response?.data?.message || `Failed to load ${config.title}.`);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [config.endpoint, config.title, http, section]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (section === "users") return;
    setSaving(true);
    setError(null);
    try {
      await http.post(config.endpoint, form);
      const res = await http.get(config.endpoint);
      setItems(res.data.items);
      setForm({ code: "", name: "", workTypeId: "", fee: "" });
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to save ${config.title}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-grid gap-3">
      <div>
        <h4 className="mb-1">{config.title}</h4>
        <p className="text-muted mb-0">
          Manage master data used across the CRM system.
        </p>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {section !== "users" ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h6 className="mb-3">Add New</h6>
            <form className="row g-3" onSubmit={onSubmit}>
              {section === "works" ? (
                <div className="col-12 col-md-4">
                  <label className="form-label">Work Type</label>
                  <select
                    className="form-select"
                    value={form.workTypeId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, workTypeId: e.target.value }))
                    }
                  >
                    <option value="">Select Work Type</option>
                    {workTypes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="col-12 col-md-4">
                <label className="form-label">{config.codeLabel}</label>
                <input
                  className="form-control"
                  value={form.code}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">{config.nameLabel}</label>
                <input
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              {section === "works" ? (
                <div className="col-12 col-md-2">
                  <label className="form-label">Fee</label>
                  <input
                    className="form-control"
                    inputMode="decimal"
                    value={form.fee}
                    onChange={(e) => setForm((prev) => ({ ...prev, fee: e.target.value }))}
                  />
                </div>
              ) : null}
              <div className="col-12 col-md-2 d-flex align-items-end">
                <button
                  className="btn btn-primary w-100"
                  type="submit"
                  disabled={!canSave || saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">List</h6>
            <span className="text-muted small">
              {loading ? "Loading..." : `${items.length} items`}
            </span>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                {section === "users" ? (
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                  </tr>
                ) : section === "works" ? (
                  <tr>
                    <th>Work Type</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Fee</th>
                  </tr>
                ) : (
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {!loading && items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={section === "users" ? 4 : section === "works" ? 4 : 2}
                      className="text-muted"
                    >
                      No records found.
                    </td>
                  </tr>
                ) : null}
                {items.map((item) =>
                  section === "users" ? (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td>{item.roleName}</td>
                      <td>{item.isActive ? "Active" : "Inactive"}</td>
                    </tr>
                  ) : section === "works" ? (
                    <tr key={item.id}>
                      <td>{item.workTypeName}</td>
                      <td className="fw-semibold">{item.code}</td>
                      <td>{item.name}</td>
                      <td>{Number(item.fee || 0).toFixed(2)}</td>
                    </tr>
                  ) : (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.code}</td>
                      <td>{item.name}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
